import { useState, useEffect, createContext, useContext, useRef, useMemo, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { PageLoader } from "@/components/LoadingSpinner";
import { PWAInstallBanner } from "@/components/PWAInstallBanner";
import { SubscriptionProvider } from "@/context/SubscriptionContext";
import { OnboardingWalkthrough } from "@/components/OnboardingWalkthrough";
import { ThemeProviderWithRouter } from "@/contexts/ThemeContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import WelcomePage from "@/pages/WelcomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import OnboardingPage from "@/pages/OnboardingPage";
import CompassPage from "@/pages/CompassPage";
import DashboardPage from "@/pages/DashboardPage";
import DiscoverPage from "@/pages/DiscoverPage";
import ReflectionsPage from "@/pages/ReflectionsPage";
import MyPathPage from "@/pages/MyPathPage";
import JournalPage from "@/pages/JournalPage";
import VaultPage from "@/pages/VaultPage";
import WeeklyResetPage from "@/pages/WeeklyResetPage";
import ProfilePage from "@/pages/ProfilePage";
import WellnessCheckPage from "@/pages/WellnessCheckPage";
import BreathingExercisePage from "@/pages/BreathingExercisePage";
import MoodTrackerPage from "@/pages/MoodTrackerPage";
import EnergyCheckInPage from "@/pages/EnergyCheckInPage";
import ThoughtReframePage from "@/pages/ThoughtReframePage";
import FocusTimerPage from "@/pages/FocusTimerPage";
import FAQPage from "@/pages/FAQPage";
import CommunityPage from "@/pages/CommunityPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminQuests from "@/pages/admin/AdminQuests";
import AdminResources from "@/pages/admin/AdminResources";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminSignals from "@/pages/admin/AdminSignals";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminTrialCodes from "@/pages/admin/AdminTrialCodes";
import AdminCommunity from "@/pages/admin/AdminCommunity";
import AdminPathfinder from "@/pages/admin/AdminPathfinder";
import AdminAudit from "@/pages/admin/AdminAudit";
import DeleteAccountPage from "@/pages/DeleteAccountPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import GrowthStudioPage from "@/pages/GrowthStudioPage";
import MeditationPage from "@/pages/MeditationPage";
import PathFinderPage from "@/pages/PathFinderPage";
import FieldKitPage from "@/pages/FieldKitPage";
import TrialPage from "@/pages/TrialPage";
import PatternsPage from "@/pages/PatternsPage";
import { InstallPrompt } from "@/components/InstallPrompt";
import { DeprecatedRouteRedirect } from "@/components/DeprecatedRouteRedirect";
import { GlobalDisclaimer } from "@/components/GlobalDisclaimer";
import { AdminLayout } from "@/components/layout/AdminLayout";

// Determine the correct backend URL based on the current domain
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // Capacitor Android/iOS environment detection
  // In Capacitor, the app runs from capacitor://localhost or https://app.compass
  // We need to use the full production URL for API calls
  const isCapacitor = typeof window !== 'undefined' && (
    window.Capacitor !== undefined || 
    protocol === 'capacitor:' ||
    hostname === 'localhost' && protocol === 'capacitor:' ||
    hostname === 'app.compass' ||
    hostname === 'localhost' && typeof window.AndroidBridge !== 'undefined'
  );
  
  if (isCapacitor) {
    // Always use production URL for Capacitor (Android/iOS) builds
    return 'https://compass.truenorth-zone.com';
  }
  
  // Preview domain
  if (hostname.includes('preview.emergentagent.com')) {
    return `https://${hostname}`;
  }
  
  // Localhost development - use env variable fallback
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_BACKEND_URL || window.location.origin;
  }
  
  // All other environments (production, custom domains, Emergent host) - use HTTPS origin
  return `https://${hostname}`;
};

const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

// Configure axios to send credentials (cookies) with all requests
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Token state kept only for UI logic (routing decisions), not for authentication
  // Actual authentication is handled via httpOnly cookies
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,  // Send cookies
        timeout: 10000
      });
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      // Only clear auth on actual authentication failures (401/403)
      // Network errors or server errors should NOT log the user out
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        setIsAuthenticated(false);
        setUser(null);
      }
      // For network errors or server errors (500, timeout, CORS, etc)
      // Preserve auth state - don't force logout on temporary issues
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, {
      withCredentials: true  // Accept cookies from response
    });
    const { user: userData } = response.data;
    setIsAuthenticated(true);
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (email, password, name, trialParams = null, privacyNoticeAccepted = true, privacyNoticeVersion = "1.0") => {
    const payload = { 
      email, 
      password, 
      name,
      // POPIA Compliance: Always include privacy notice acceptance
      privacy_notice_accepted: privacyNoticeAccepted,
      privacy_notice_version: privacyNoticeVersion
    };
    
    // Include trial params if provided (from companion app referral / PayFast)
    if (trialParams) {
      payload.trial_params = trialParams;
    }
    
    const response = await axios.post(`${API}/auth/register`, payload, {
      withCredentials: true
    });
    const { user: userData, trial } = response.data;
    setIsAuthenticated(true);
    setUser(userData);
    return { user: userData, trial };
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true,
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  // Note: 'token' is kept as alias to isAuthenticated for backwards compatibility
  const contextValue = useMemo(() => ({
    user, 
    token: isAuthenticated,  // Boolean flag for route protection
    loading, 
    login, 
    register, 
    logout, 
    updateUser, 
    checkAuth
  }), [user, isAuthenticated, loading, login, register, logout, updateUser, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireOnboarding = true, adminOnly = false }) => {
  const { user, token: isAuthenticated, loading, checkAuth } = useAuth();
  const location = useLocation();
  const [retrying, setRetrying] = useState(false);

  // If we're authenticated but no user data, try to fetch user data once
  useEffect(() => {
    if (isAuthenticated && !user && !loading && !retrying) {
      setRetrying(true);
      checkAuth().finally(() => setRetrying(false));
    }
  }, [isAuthenticated, user, loading, retrying, checkAuth]);

  // Memoize navigation state to prevent unnecessary object creation
  const navigationState = useMemo(() => ({ from: location }), [location]);

  if (loading || retrying) {
    return <PageLoader message="Finding your path" />;
  }

  // Not authenticated - need to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={navigationState} replace />;
  }

  // Authenticated but no user data - show loader while we wait
  if (!user) {
    return <PageLoader message="Restoring session" />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/compass" replace />;
  }

  if (requireOnboarding && !user.onboarding_complete && user.role !== "admin") {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Walkthrough Wrapper - shows walkthrough for first-time users after onboarding
const WalkthroughWrapper = ({ children }) => {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [walkthroughChecked, setWalkthroughChecked] = useState(false);

  // Routes where walkthrough should not be shown
  const walkthroughExcludedRoutes = ['/delete-account', '/profile', '/settings', '/forgot-password', '/reset-password'];

  useEffect(() => {
    // Only show walkthrough if:
    // 1. User exists
    // 2. User has completed onboarding
    // 3. User hasn't completed walkthrough yet
    // 4. User is not admin
    // 5. Not on excluded routes (account management pages)
    const isExcludedRoute = walkthroughExcludedRoutes.some(route => location.pathname.startsWith(route));
    
    if (user && user.onboarding_complete && !user.walkthrough_completed && user.role !== "admin" && !isExcludedRoute) {
      setShowWalkthrough(true);
    } else {
      setShowWalkthrough(false);
    }
    setWalkthroughChecked(true);
  }, [user, location.pathname]);

  const handleWalkthroughComplete = () => {
    setShowWalkthrough(false);
    if (user) {
      updateUser({ ...user, walkthrough_completed: true });
    }
  };

  if (!walkthroughChecked) {
    return children;
  }

  return (
    <>
      {children}
      {showWalkthrough && <OnboardingWalkthrough onComplete={handleWalkthroughComplete} />}
    </>
  );
};

// Auth Callback Component for Google OAuth
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const { updateUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, {}, {
            headers: { "X-Session-ID": sessionId },
            withCredentials: true,
          });
          
          updateUser(response.data.user);
          
          // Clear hash and navigate
          window.history.replaceState(null, "", window.location.pathname);
          
          if (!response.data.user.onboarding_complete) {
            navigate("/onboarding", { replace: true });
          } else if (response.data.user.role === "admin") {
            navigate("/admin", { replace: true });
          } else {
            navigate("/compass", { replace: true });
          }
        } catch (error) {
          console.error("Auth callback error:", error);
          navigate("/login", { replace: true });
        }
      }
    };

    processSession();
  }, [navigate, updateUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-pulse text-primary text-xl font-heading mb-4">Authenticating...</div>
        <p className="text-muted-foreground">Please wait while we sign you in.</p>
      </div>
    </div>
  );
};

// Main App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for OAuth callback session_id in URL fragment
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/wellness-check" element={<WellnessCheckPage />} />
      <Route path="/breathe" element={<BreathingExercisePage />} />
      <Route path="/mood-tracker" element={<MoodTrackerPage />} />
      <Route path="/energy-check-in" element={<EnergyCheckInPage />} />
      <Route path="/thought-reframe" element={<ThoughtReframePage />} />
      <Route path="/focus-timer" element={<FocusTimerPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/growth-studio" element={<GrowthStudioPage />} />
      <Route path="/meditation" element={<MeditationPage />} />
      <Route path="/trial" element={<TrialPage />} />
      <Route path="/meditation/:categoryId" element={<MeditationPage />} />
      <Route
        path="/pathfinder"
        element={
          <ProtectedRoute>
            <PathFinderPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patterns"
        element={
          <ProtectedRoute>
            <PatternsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quest/:questSlug"
        element={
          <ProtectedRoute>
            <FieldKitPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute requireOnboarding={false}>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compass"
        element={
          <ProtectedRoute>
            <CompassPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/discover"
        element={
          <ProtectedRoute>
            <DiscoverPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reflections"
        element={
          <ProtectedRoute>
            <ReflectionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      {/* My Path - Deprecated, redirect to Discover/Quests */}
      <Route
        path="/path"
        element={
          <ProtectedRoute>
            <DeprecatedRouteRedirect 
              message="My Path has evolved into Quests"
              redirectTo="/discover"
              delay={5000}
              showInfo={true}
            />
          </ProtectedRoute>
        }
      />
      {/* Deprecated Routes - Redirect to Discover */}
      <Route
        path="/course/:courseId"
        element={
          <ProtectedRoute>
            <DeprecatedRouteRedirect 
              message="Courses have evolved into Quests"
              redirectTo="/discover"
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/course/:courseId/:moduleId"
        element={
          <ProtectedRoute>
            <DeprecatedRouteRedirect 
              message="Courses have evolved into Quests"
              redirectTo="/discover"
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lesson/:lessonId"
        element={
          <ProtectedRoute>
            <DeprecatedRouteRedirect 
              message="Lessons are now part of external Quests"
              redirectTo="/discover"
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/journal"
        element={
          <ProtectedRoute>
            <JournalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vault"
        element={
          <ProtectedRoute>
            <VaultPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/weekly-reset"
        element={
          <ProtectedRoute>
            <WeeklyResetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/delete-account"
        element={
          <ProtectedRoute>
            <DeleteAccountPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/community"
        element={
          <ProtectedRoute>
            <CommunityPage />
          </ProtectedRoute>
        }
      />
      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/members"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminMembers />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/quests"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminQuests />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      {/* Redirect old admin/courses to admin/quests */}
      <Route
        path="/admin/courses"
        element={<Navigate to="/admin/quests" replace />}
      />
      <Route
        path="/admin/resources"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminResources />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/signals"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminSignals />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminAnalytics />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/announcements"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminAnnouncements />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/trial-codes"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminTrialCodes />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pathfinder"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminPathfinder />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminAudit />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/community"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminCommunity />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProviderWithRouter>
        <AuthProvider>
          <SubscriptionProvider>
            <WalkthroughWrapper>
              <AppRouter />
            </WalkthroughWrapper>
            <GlobalDisclaimer />
            <InstallPrompt />
            <PWAInstallBanner />
            <Toaster position="top-right" richColors />
          </SubscriptionProvider>
        </AuthProvider>
      </ThemeProviderWithRouter>
    </BrowserRouter>
  );
}

export default App;
